import { CITIES, INSURANCES, SPECIALTIES } from "@/data/heallexa";
import { US_STATES } from "@/lib/us-states";

/** "Texas" -> "texas", "New York" -> "new-york", "District of Columbia" -> "district-of-columbia" */
export function stateNameSlug(name: string): string {
  return toSlug(name);
}

/** Resolve "/locations/{slug}" to a state record. Accepts full-name slug or 2-letter code. */
export function stateFromNameSlug(slug: string): { code: string; name: string } | undefined {
  const s = slug.toLowerCase();
  return (
    US_STATES.find((st) => stateNameSlug(st.name) === s) ||
    US_STATES.find((st) => st.code.toLowerCase() === s)
  );
}

export function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function citySlug(city: string): string {
  // "Dallas, TX" -> "dallas-tx"
  return toSlug(city);
}

export function cityFromSlug(slug: string): string | undefined {
  const s = slug.toLowerCase();
  return CITIES.find((c) => citySlug(c) === s);
}

export function specialtySlug(specialty: string): string {
  return toSlug(specialty);
}

export function specialtyOverviewPath(specialty: string): string {
  return `/specialties/${specialtySlug(specialty)}`;
}

export function specialtyFromSlug(slug: string): string | undefined {
  const s = slug.toLowerCase();
  return SPECIALTIES.find((sp) => specialtySlug(sp) === s);
}

/** Title-case a slug as a fallback display label when not in curated list. */
export function titleCaseFromSlug(slug: string): string {
  return slug
    .toLowerCase()
    .split("-")
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

/** Resolve a specialty slug, falling back to a title-cased label for unknown taxonomies. */
export function resolveSpecialtyFromSlug(slug: string): string {
  return specialtyFromSlug(slug) ?? titleCaseFromSlug(slug);
}

/** Resolve a city slug, falling back to a title-cased label for unknown cities. */
export function resolveCityFromSlug(slug: string): string {
  return cityFromSlug(slug) ?? titleCaseFromSlug(slug);
}

export function insuranceFromSlug(slug: string) {
  const s = slug.toLowerCase();
  return INSURANCES.find((i) => i.id === s);
}

/** SEO-friendly insurance slugs surfaced in sitemap and routes. */
export const SEO_INSURANCE_IDS = [
  "aetna",
  "bcbs",
  "uhc",
  "cigna",
  "humana",
  "kaiser",
  "anthem",
  "medicare",
  "medicaid",
  "tricare",
] as const;

/** Cities prioritized for /specialty/$slug/$city pages. */
export const SEO_CITIES = CITIES;

/** Specialties prioritized for SEO landing pages. */
export const SEO_SPECIALTIES = SPECIALTIES;
